import { useEffect, useRef, useState } from "react";
import { Table } from "@tanstack/react-table";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

import { Button } from "../Button";

export interface BulkAction {
  label: string;
  /** clearSelection is for actions that finish later, e.g. after a modal or a request. */
  onClick: (selectedRows: any[], clearSelection: () => void) => void;
}

export interface TableBulkActionsProps {
    table: Table<any>;
    bulkActions: BulkAction[];
    /** Groups the actions behind one toggle button with this label, as Core's tables do. */
    groupLabel?: string;
}

export default function TableBulkActions({ bulkActions, table, groupLabel }: TableBulkActionsProps) {
    const selectedRows = table.getSelectedRowModel().flatRows;
    const selectedCount = selectedRows.length;
    const [groupOpen, setGroupOpen] = useState(false);
    const prevSelectedCountRef = useRef(0);

    // Same as Core's bulk action group: opens as soon as rows are selected, and
    // closes again when the selection is cleared.
    useEffect(() => {
        if (prevSelectedCountRef.current === 0 && selectedCount > 0) setGroupOpen(true);
        if (selectedCount === 0) setGroupOpen(false);
        prevSelectedCountRef.current = selectedCount;
    }, [selectedCount]);

    if (!bulkActions || bulkActions.length === 0 || !table) return null;

    const runAction = (action: BulkAction) =>
        action.onClick(
            selectedRows.map(row => row.original),
            () => table.resetRowSelection(),
        );

    if (groupLabel) {
        return (
            <div className="mb-4">
                <div className="flex items-center gap-3">
                    <Button
                        className="flex items-center gap-2"
                        disabled={selectedCount === 0}
                        onClick={() => setGroupOpen(open => !open)}
                    >
                        {groupLabel}
                        {groupOpen ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                    </Button>
                    {selectedCount > 0 && (
                        <span className="text-xs italic text-orange-700">
                            {selectedCount} selected
                        </span>
                    )}
                </div>
                {groupOpen && selectedCount > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        {bulkActions.map(action => (
                            <Button
                                key={action.label}
                                variant="outlined"
                                onClick={() => runAction(action)}
                            >
                                {action.label}
                            </Button>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="flex justify-between mb-4">
            {bulkActions && bulkActions.map((action, index) => (
            <button
                key={index}
                className={`
                    rounded px-3 py-2 font-medium text-sm text-white border
                    ${
                    selectedCount === 0
                    ? "bg-gray-200 border-gray-600/10"
                    : "bg-orange-600/80 hover:bg-orange-600 border-orange-600/80 hover:border-orange-600"
                }`}
                type="button"
                disabled={selectedCount === 0}
                onClick={() => runAction(action)}
                >
                {action.label}
            </button>
            ))}
        </div>
    );
}
